'use client';

import { Box } from '@mui/material';
import { calculatePriceStats } from '@web/utils/priceRating';
import { currencyFormatter } from '@web/utils/currency';
import { format } from 'date-fns';
import { TimeRange } from '@web/app/components/TimeRangeControls';
import { filterByTimeRange } from '@web/utils/timeRange';

interface PriceRatingProps {
  currentPrice: number | null;
  priceHistory: any[];
  timeRange: TimeRange;
  viewport?: 'mobile' | 'desktop';
}

const GRADIENT_COLORS = {
  start: '#66bb6a', // green
  lowMid: '#81c784', // light green
  middle: '#ffb74d', // orange
  highMid: '#e57373', // light red
  end: '#ef5350', // red
};

export default function PriceRating({
  currentPrice = 0,
  priceHistory = [],
  timeRange,
  viewport = 'desktop',
}: PriceRatingProps) {
  const filteredHistory = priceHistory
    ? filterByTimeRange(priceHistory, timeRange)
    : [];
  const priceStats =
    filteredHistory.length > 0
      ? calculatePriceStats(currentPrice || 0, filteredHistory)
      : null;

  // Create stable ID based on viewport
  const gradientId = `priceGradient-${viewport}`;

  const getColorForPercentage = (percentage: number): string => {
    if (percentage <= 25) return GRADIENT_COLORS.start;
    if (percentage <= 40) return GRADIENT_COLORS.lowMid;
    if (percentage <= 60) return GRADIENT_COLORS.middle;
    if (percentage <= 80) return GRADIENT_COLORS.highMid;
    return GRADIENT_COLORS.end;
  };

  return (
    <Box position="relative" height={80}>
      <svg width="100%" height="100%" preserveAspectRatio="xMidYMid meet">
        {/* Background */}
        <rect x="5%" y={30} width="90%" height={20} fill="#eee" rx={10} />

        {/* Gradient */}
        <defs>
          <linearGradient id={gradientId} x1="0%" x2="100%">
            <stop offset="0%" stopColor={GRADIENT_COLORS.start} />
            <stop offset="30%" stopColor={GRADIENT_COLORS.lowMid} />
            <stop offset="50%" stopColor={GRADIENT_COLORS.middle} />
            <stop offset="70%" stopColor={GRADIENT_COLORS.highMid} />
            <stop offset="100%" stopColor={GRADIENT_COLORS.end} />
          </linearGradient>
        </defs>
        <rect
          x="5%"
          y={30}
          width="90%"
          height={20}
          fill={`url(#${gradientId})`}
          rx={10}
        />

        {priceStats && (
          <>
            {/* Average price marker */}
            <line
              x1={`${
                5 +
                90 *
                  ((priceStats.average - priceStats.allTimeLow) /
                    (priceStats.allTimeHigh - priceStats.allTimeLow))
              }%`}
              y1={20}
              x2={`${
                5 +
                90 *
                  ((priceStats.average - priceStats.allTimeLow) /
                    (priceStats.allTimeHigh - priceStats.allTimeLow))
              }%`}
              y2={60}
              stroke="#666"
              strokeWidth={2}
              strokeDasharray="3"
            />

            {/* Current price indicator */}
            <g>
              {/* Vertical line */}
              <line
                x1={`${5 + (90 * priceStats.percentFromLow) / 100}%`}
                y1={20}
                x2={`${5 + (90 * priceStats.percentFromLow) / 100}%`}
                y2={52}
                stroke={getColorForPercentage(priceStats.percentFromLow)}
                strokeWidth={2}
                strokeDasharray="3"
              />

              {/* Current price label */}
              <text
                x={`${5 + (90 * priceStats.percentFromLow) / 100}%`}
                y={15}
                fontSize={12}
                textAnchor={
                  priceStats.percentFromLow < 10
                    ? 'start'
                    : priceStats.percentFromLow > 90
                    ? 'end'
                    : 'middle'
                }
                fill={getColorForPercentage(priceStats.percentFromLow)}
                fontWeight="bold"
              >
                Today: {currencyFormatter.format(currentPrice || 0)}
              </text>
            </g>

            {/* Price labels */}
            <g>
              <text
                x="5%"
                y={65}
                fontSize={12}
                textAnchor="start"
                fill="#66bb6a"
                fontWeight="bold"
              >
                Lowest: {currencyFormatter.format(priceStats.allTimeLow)}
              </text>
              <text
                x="5%"
                y={80}
                fontSize={11}
                textAnchor="start"
                fill="#66bb6a"
                opacity={0.8}
              >
                {format(new Date(priceStats.lowestDate), 'MMM d, yyyy')}
              </text>
            </g>

            {/* Average price label */}
            <g>
              {(() => {
                const avgPercent =
                  ((priceStats.average - priceStats.allTimeLow) /
                    (priceStats.allTimeHigh - priceStats.allTimeLow)) *
                  100;

                // Calculate distances to other labels
                const distToLow = avgPercent;
                const distToHigh = 100 - avgPercent;
                const distToCurrent = Math.abs(
                  avgPercent - priceStats.percentFromLow
                );

                // Position vertically based on proximity
                let yPosition = 65; // Default position
                if (distToLow < 15 || distToHigh < 15 || distToCurrent < 15) {
                  yPosition = 45; // Move up if close to other labels
                }

                // Calculate x position with proper scaling
                const xPos = 5 + avgPercent * 0.9;

                return (
                  <>
                    <line
                      x1={`${xPos}%`}
                      y1={20}
                      x2={`${xPos}%`}
                      y2={60}
                      stroke="#666"
                      strokeWidth={2}
                      strokeDasharray="3"
                    />
                    <text
                      x={`${xPos}%`}
                      y={yPosition}
                      fontSize={12}
                      textAnchor="middle"
                      fill="#666"
                      fontWeight="bold"
                    >
                      Average: {currencyFormatter.format(priceStats.average)}
                    </text>
                  </>
                );
              })()}
            </g>

            <g>
              <text
                x="95%"
                y={65}
                fontSize={12}
                textAnchor="end"
                fill="#ef5350"
                fontWeight="bold"
              >
                Highest: {currencyFormatter.format(priceStats.allTimeHigh)}
              </text>
              <text
                x="95%"
                y={80}
                fontSize={11}
                textAnchor="end"
                fill="#ef5350"
                opacity={0.8}
              >
                {format(new Date(priceStats.highestDate), 'MMM d, yyyy')}
              </text>
            </g>
          </>
        )}
      </svg>
    </Box>
  );
}
